import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Helmet } from "react-helmet-async";

/**
 * <FaqSection /> — visible FAQ accordion + FAQPage JSON-LD.
 *
 * Renders both the on-page FAQ (great for humans + on-page keyword coverage)
 * and the FAQPage schema (rich snippet in Google, Bing and AI search).
 */
export default function FaqSection({ kicker = "FAQ", title = "Frequently asked questions", items = [], testidRoot = "faq" }) {
  const [open, setOpen] = useState(0);

  if (!items || items.length === 0) return null;

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: it.a,
      },
    })),
  };

  return (
    <section className="py-14 md:py-20" data-testid={`${testidRoot}-section`}>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </Helmet>

      <div className="max-w-4xl mx-auto px-6">
        <div className="max-w-2xl">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">{kicker}</div>
          <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
            {title}
          </h2>
        </div>
        <div className="mt-10 divide-y divide-border rounded-3xl border border-border bg-card">
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <div key={it.q} data-testid={`${testidRoot}-item-${i}`}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full text-left px-6 py-5 md:py-6 flex items-start justify-between gap-6 group"
                  aria-expanded={isOpen}
                >
                  <span className="font-heading font-semibold text-base md:text-lg leading-snug pr-8">
                    {it.q}
                  </span>
                  <span
                    className={`shrink-0 mt-0.5 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center transition-transform ${
                      isOpen ? "rotate-45" : ""
                    }`}
                  >
                    <Plus className="w-4 h-4" />
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28, ease: "easeOut" }}
                      className="overflow-hidden"
                    >
                      <div
                        className="px-6 pb-6 -mt-2 text-sm md:text-base text-muted-foreground leading-relaxed"
                        data-testid={`${testidRoot}-answer-${i}`}
                      >
                        {it.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
